/**
 * How the deviation is measured (spec §4.2, §12.5, §12.6): a radio group —
 * arrow keys move between the modes — with the §12.6 labels, laid out 2×2
 * when the dialog is wide enough and in one column below that. Every mode
 * shows its one-line hint (muted), so the choice can be made by reading, and
 * each item is described by its own hint.
 *
 * A mode the design cannot support stays visible and disabled; the reason is
 * printed ONCE under the group and every disabled item points at it too
 * (`aria-describedby` = its hint, then the reason). The reason comes from the model
 * (`deviationModeRestriction`), so it cannot drift from the rule. A design
 * that allows a single mode (a point cloud: vertical only, measured against
 * a DSM) gets that reason as a note instead of a one-option control.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DEVIATION_MODES, deviationModeRestriction, type DeviationMode } from '@/lib/analyses/compare/availability';
import type { LayerLike } from '@/lib/analyses/candidates';
import { translateKey } from '@/lib/analyses/translateKey';
import { cn } from '@/lib/utils';

const MODE_KEYS = {
  vertical: { label: 'compareSurfaces.modes.vertical.label', help: 'compareSurfaces.modes.vertical.help' },
  normal: { label: 'compareSurfaces.modes.normal.label', help: 'compareSurfaces.modes.normal.help' },
  nearest: { label: 'compareSurfaces.modes.nearest.label', help: 'compareSurfaces.modes.nearest.help' },
  horizontal: { label: 'compareSurfaces.modes.horizontal.label', help: 'compareSurfaces.modes.horizontal.help' },
} as const satisfies Record<DeviationMode, { label: string; help: string }>;

export interface DeviationModeFieldProps {
  value: DeviationMode;
  onChange: (mode: DeviationMode) => void;
  /** The picked design (role A); undefined while none is picked. */
  design?: LayerLike;
}

export function DeviationModeField({ value, onChange, design }: DeviationModeFieldProps) {
  const { t } = useTranslation('project');
  const id = useId();
  const { modes, reasonKey } = deviationModeRestriction(design);
  const reason = reasonKey ? translateKey(t, reasonKey) : null;

  if (modes.length === 1) {
    return (
      <p data-testid='compare-deviation-mode-pc' className='rounded-md border border-border p-3 text-sm'>
        {reason}
      </p>
    );
  }

  const reasonId = `${id}-reason`;
  return (
    // A container query, not a viewport breakpoint: the dialog is narrower
    // than the window it sits in.
    <div className='@container flex min-w-0 flex-col gap-2'>
      <span id={`${id}-label`} className='text-sm font-medium'>
        {t('compareSurfaces.settings.measure')}
      </span>
      <RadioGroup
        data-testid='compare-deviation-mode'
        aria-labelledby={`${id}-label`}
        value={value}
        onValueChange={(v) => onChange(v as DeviationMode)}
        className='grid grid-cols-1 gap-x-4 gap-y-2 @min-[420px]:grid-cols-2'>
        {DEVIATION_MODES.map((m) => {
          const disabled = !modes.includes(m);
          const itemId = `${id}-${m}`;
          const describedBy = disabled && reason ? `${itemId}-help ${reasonId}` : `${itemId}-help`;
          return (
            <div key={m} className={cn('flex min-w-0 items-start gap-2', disabled && 'opacity-60')}>
              <RadioGroupItem
                id={itemId}
                value={m}
                disabled={disabled}
                aria-labelledby={`${itemId}-label`}
                aria-describedby={describedBy}
                className='mt-0.5'
              />
              <div className='flex min-w-0 flex-col gap-0.5'>
                <Label id={`${itemId}-label`} htmlFor={itemId} className='font-normal'>
                  {t(MODE_KEYS[m].label)}
                </Label>
                <span id={`${itemId}-help`} className='text-xs break-words text-muted-foreground'>
                  {t(MODE_KEYS[m].help)}
                </span>
              </div>
            </div>
          );
        })}
      </RadioGroup>
      {reason && (
        <p id={reasonId} data-testid='compare-deviation-mode-reason' className='text-xs break-words text-muted-foreground'>
          {reason}
        </p>
      )}
    </div>
  );
}
